import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';
import type { User, UserRole } from '@/lib/types';

export interface RegisterData {
  email: string;
  password: string;
  confirmPassword?: string;
  firstName: string;
  lastName: string;
  role: 'PATIENT' | 'PRACTITIONER_PENDING';
  dateOfBirth?: string;
  rppsNumber?: string;
  /**
   * Issue #70 : champs OBLIGATOIRES du contrat serveur pour un praticien.
   * `RegisterRequest` (PractitionerController) exige specialty, city et postalCode :
   * les omettre renvoyait 400 « doit être renseigné ». Le formulaire ne les collectait
   * pas — l'inscription praticien ne pouvait donc jamais aboutir.
   */
  specialty?: string;
  subSpecialty?: string;
  city?: string;
  postalCode?: string;
  sector?: 'SECTOR1' | 'SECTOR2' | 'OPTAM' | 'NON_CONVENTIONNE';
  acceptsCgu: boolean;
  acceptsPrivacy: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface MFAData {
  code: string;
}

export interface ResetPasswordData {
  email: string;
}

export interface ConfirmResetData {
  token: string;
  newPassword: string;
}

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isMFARequired: boolean;
  error: string | null;
  
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<string>;
  verifyEmail: (token: string) => Promise<boolean>;
  resetPassword: (data: ResetPasswordData) => Promise<void>;
  confirmResetPassword: (data: ConfirmResetData) => Promise<void>;
  verifyMFA: (data: MFAData) => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  clearError: () => void;
}

function getTokenFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  // B7 (issue #42) : localStorage persistant multi-onglets. Le refresh token
  // reste en cookie httpOnly gote par le client axios (withCredentials), ce
  // qui permet a un nouvel onglet de relancer la session via /auth/refresh.
  return localStorage.getItem('access_token');
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: getTokenFromStorage(),
      isAuthenticated: !!getTokenFromStorage(),
      isLoading: false,
      isMFARequired: false,
      error: null,
      
      // Login
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null, isMFARequired: false });
        
        try {
          const response = await api.post<{ user: User; accessToken: string; requiresMFA?: boolean }>('/auth/login', credentials);
          const { user, accessToken, requiresMFA } = response.data;
          
          if (requiresMFA) {
            set({ 
              user, 
              token: null, 
              isAuthenticated: false,
              isMFARequired: true,
              isLoading: false,
            });
            return;
          }
          
          if (typeof window !== 'undefined') {
            localStorage.setItem('access_token', accessToken);
          }
          
          set({ 
            user, 
            token: accessToken, 
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          // Issue #74 : l'API renvoie {code, error, message, requestId} ou {error}.
          // Lire `error` en priorite (message utilisateur), puis `message`, puis le statut.
          const data = error.response?.data;
          const status = error.response?.status;

          let errorMessage = data?.error || data?.message;
          if (!errorMessage) {
            errorMessage = status === 401
              ? 'Email ou mot de passe incorrect.'
              : 'Connexion impossible. Veuillez réessayer.';
          }

          set({ 
            error: errorMessage,
            isLoading: false,
            isMFARequired: false,
          });
          throw error;
        }
      },
      
      // Register
      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });

        try {
          // Issue #70 : le payload envoyé doit correspondre EXACTEMENT au contrat serveur.
          // `RegisterData` est un type d'interface (il porte confirmPassword, role,
          // acceptsCgu...), pas le contrat de l'API. Envoyer l'objet tel quel marchait
          // « par chance » pour un patient (les champs en trop sont ignorés) mais échouait
          // pour un praticien : specialty, city et postalCode sont @NotBlank côté serveur
          // et n'étaient jamais transmis -> 400.
          //
          // On construit donc le corps explicitement, par endpoint.
          const isPractitioner = data.role === 'PRACTITIONER_PENDING';
          const endpoint = isPractitioner
            ? '/practitioners/register'
            : '/users/register';

          const payload = isPractitioner
            ? {
                email: data.email,
                password: data.password,
                firstName: data.firstName,
                lastName: data.lastName,
                rppsNumber: data.rppsNumber,
                specialty: data.specialty,
                subSpecialty: data.subSpecialty || undefined,
                city: data.city,
                postalCode: data.postalCode,
                sector: data.sector,
              }
            : {
                email: data.email,
                password: data.password,
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: data.dateOfBirth || undefined,
              };

          const response = await api.post(endpoint, payload, {
            headers: {
              'Idempotency-Key': crypto.randomUUID(),
            },
          });
          
          // After successful registration, auto-login if response contains token
          if (response.data.accessToken) {
            if (typeof window !== 'undefined') {
              localStorage.setItem('access_token', response.data.accessToken);
            }
            set({
              user: response.data.user,
              token: response.data.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Inscription échouée. Veuillez réessayer.';
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },
      
      // Logout
      logout: () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token');
        }
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          isMFARequired: false,
          error: null,
        });
      },
      
      // Refresh Token
      refreshToken: async () => {
        try {
          const response = await api.post<{ accessToken: string }>('/auth/refresh', {});
          const { accessToken } = response.data;
          
          if (typeof window !== 'undefined') {
            localStorage.setItem('access_token', accessToken);
          }
          
          set({ token: accessToken });
          return accessToken;
        } catch (error) {
          get().logout();
          throw error;
        }
      },
      
      // Verify Email
      verifyEmail: async (token: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          await api.get(`/users/verify-email?token=${encodeURIComponent(token)}`);
          set({ isLoading: false });
          return true;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Vérification de l\'email échouée.';
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          return false;
        }
      },
      
      // Reset Password (request)
      resetPassword: async (data: ResetPasswordData) => {
        set({ isLoading: true, error: null });
        
        try {
          await api.post('/auth/forgot-password', data);
          set({ isLoading: false });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Impossible d\'envoyer le lien de réinitialisation.';
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },
      
      // Confirm Reset Password
      confirmResetPassword: async (data: ConfirmResetData) => {
        set({ isLoading: true, error: null });
        
        try {
          await api.post('/auth/reset-password', data);
          set({ isLoading: false });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Réinitialisation du mot de passe échouée.';
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },
      
      // Verify MFA
      verifyMFA: async (data: MFAData) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await api.post<{ accessToken: string }>('/auth/mfa/verify', data);
          const { accessToken } = response.data;
          
          if (typeof window !== 'undefined') {
            localStorage.setItem('access_token', accessToken);
          }
          
          // Update user with MFA enabled status
          const currentUser = get().user;
          if (currentUser) {
            set({ 
              token: accessToken, 
              isAuthenticated: true,
              isMFARequired: false,
              user: { ...currentUser, mfaEnabled: true },
              isLoading: false,
            });
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Code MFA invalide.';
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },
      
      // Update User
      updateUser: (user: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...user } : null,
        }));
      },
      
      // Clear Error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper functions
export function isAuthenticated(): boolean {
  return useAuthStore.getState().isAuthenticated;
}

export function getCurrentUser(): User | null {
  return useAuthStore.getState().user;
}

export function hasRole(role: UserRole): boolean {
  const user = getCurrentUser();
  return user?.role === role;
}

export function hasAnyRole(roles: UserRole[]): boolean {
  const user = getCurrentUser();
  return user ? roles.includes(user.role) : false;
}